import type { ReactElement } from 'react';

import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import styled, { css } from 'styled-components';

import { USPCountdown } from './USPCountdown';
import { USPItem } from './USPItem';
import { USPItemContainer } from './USPItemContainer';
import {
  USP_ANIMATION_DURATION,
  USP_MESSAGE_DISPLAY_TIME,
  USP_BAR_DEFAULT_COLOR_SCHEME,
  timeOffsets,
} from '../../../utilities/constants/usp';
import { getPpeJourneyContent } from '../../../utilities/helpers/getPpeJourneyContent';
import { colors } from '../core/styles';
import type {
  TimeZone,
  Title as USPCountdownSchema,
} from '~/amplienceTypes/schemas/exported/usp-countdown-schema';
import type { Title as USPItemSchema } from '~/amplienceTypes/schemas/exported/usp-item-schema';
import type {
  Title as UspList,
  ColorScheme,
} from '~/amplienceTypes/schemas/exported/usp-list-schema';
import { PageTypes } from '~/utilities/constants';
import { useGalleryContext } from '~/utilities/context/dynamic/GalleryContext';
import { useMediaQueryContext } from '~/utilities/context/dynamic/MediaQueryContext';
import { useUserContext } from '~/utilities/context/dynamic/UserContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { usePDPContext } from '~/utilities/context/static/PDPContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { SiteType } from '~/utilities/graphql/codegen';

const fullCycleTime = USP_ANIMATION_DURATION + USP_MESSAGE_DISPLAY_TIME + USP_ANIMATION_DURATION;

const LightUspConfig: Record<SiteType, boolean> = {
  [SiteType.Gstar]: true,
  [SiteType.Outlet]: true,
  [SiteType.Employeeshop]: false,
  [SiteType.Aaf]: false,
  [SiteType.Wahts]: false,
};

const S = {
  Wrapper: styled.div<{
    $colorScheme?: ColorScheme;
    $isSticky?: boolean;
    $isVisible?: boolean;
  }>`
    ${({ $colorScheme, theme, $isSticky, $isVisible }) => css`
      ${$isSticky &&
      css`
        position: sticky;
        top: calc((var(--top-nav-height) + var(--smart-banner-height)));
        z-index: 1000;
      `}
      ${LightUspConfig[theme.siteType] &&
      $colorScheme === 'Light (default)' &&
      css`
        box-sizing: border-box;
        border-bottom: ${theme.borders.width.xs} solid ${theme.colors.border.subtle};
      `}
      opacity: ${$isVisible ? '1' : '0'};
      transition: ${$isVisible ? 'all 0.2s ease-in-out' : 'unset'};
      height: var(--usp-bar-height);
      width: 100%;
      text-align: center;
      overflow: hidden;
      display: block;
      background-color: ${({ theme }) => theme.colors.background.base};

      ${($colorScheme === 'Black Friday' &&
        css`
          background-color: ${colors.BLACK};
        `) ||
      ($colorScheme === 'Dark' &&
        css`
          background-color: ${colors.NERO_GREY};
        `) ||
      ($colorScheme === 'PPE' &&
        css`
          background-color: ${colors.RRP_RED};
        `)}

      &.zeroHeight {
        height: 0;
      }

      #container:has(.ppeBanner) &:not(.sticky) {
        display: none;
      }
    `}
  `,

  USPBarList: styled.ul`
    list-style-type: none;
    margin: 0;
    padding: 0;
  `,
  PpeUspCountdown: styled(USPCountdown)<{ $colorScheme?: ColorScheme }>`
    ${({ theme, $colorScheme }) =>
      $colorScheme === USP_BAR_DEFAULT_COLOR_SCHEME &&
      css`
        &,
        & * {
          color: ${theme.colors.content['sale-alt']} !important;
          font-size: ${theme.font.size[2]};
          font-weight: ${theme.variables.font.weight.medium};
        }
      `}
  `,
};

const STICKY_USP_PAGES = new Set([PageTypes.CATEGORY, PageTypes.PRODUCT, PageTypes.SEARCH]);

export const HeaderUSP = ({
  usp,
  colorScheme: initialColorScheme = USP_BAR_DEFAULT_COLOR_SCHEME,
}: UspList): ReactElement => {
  const {
    configuration: { enableUspScroll },
    ppeContent,
    pageType,
  } = useStaticContext();
  const { isMobile, isMobileOrTablet } = useMediaQueryContext();
  const {
    product: { salesDiscountsEndDate },
  } = usePDPContext();
  const { galleryZoomIn } = useGalleryContext();
  const { user } = useUserContext();
  const { language } = useAppContext();

  const [colorScheme, setColorScheme] = useState<ColorScheme>(initialColorScheme);
  const [isSticky, setIsSticky] = useState(STICKY_USP_PAGES.has(pageType));
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [ppeUspCountdown, setPpeUspCountdown] = useState<{
    countdown?: { endDate: Date };
    text?: string;
  }>({});
  const fullHeightGalleryZoomIn = isMobileOrTablet && isSticky && galleryZoomIn;

  const uspBarRef = useRef<HTMLDivElement>(null);

  const convertStringToDate = useCallback(
    (endDateStr: string, timezone: TimeZone) =>
      new Date(`${endDateStr.replace(' ', 'T')}${timeOffsets[timezone]}`),
    []
  );

  const usps = useMemo(() => {
    const regex = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/;

    return (Array.isArray(usp) ? (usp as (USPCountdownSchema & USPItemSchema)[]) : []).filter(
      item => {
        if (!item) {
          return false;
        }

        if (item.countdown?.endDate) {
          const isInvalidEndDate = !regex.test(item.countdown.endDate);
          const endTime = convertStringToDate(
            item.countdown.endDate,
            item.countdown.timezone ?? 'UTC+2 (CEST)'
          );

          if (isInvalidEndDate || Math.floor((endTime.getTime() - Date.now()) / 1000) < 0) {
            return false;
          }

          return item.text && item.text !== '';
        }

        return item.text?.title && item.text?.title !== '';
      }
    );
  }, [usp, convertStringToDate]);

  useEffect(() => {
    if (!user || !language || !ppeContent) {
      return;
    }

    const handlePpeJourney = (language: string) => {
      const { expirationDate, formattedValue, trafficSource } = user.personalisedDiscount ?? {};

      if (!expirationDate || salesDiscountsEndDate || ppeUspCountdown.text) {
        setIsVisible(true);

        return;
      }

      const ppeJourneyContent = getPpeJourneyContent({
        ppeContent,
        trafficSource,
        expirationDate,
        formattedValue,
        language,
      });

      if (ppeJourneyContent?.uspCountdown) {
        setPpeUspCountdown(ppeJourneyContent.uspCountdown);

        if (ppeJourneyContent?.colorScheme) {
          setColorScheme(ppeJourneyContent.colorScheme);
          setIsSticky(true);
        }
      }

      setIsVisible(true);
    };

    handlePpeJourney(language);
  }, [
    user,
    salesDiscountsEndDate,
    language,
    ppeUspCountdown.text,
    ppeUspCountdown.countdown?.endDate,
    pageType,
    ppeContent,
  ]);

  useEffect(() => {
    const handleScroll = () => {
      if (!(pageType === PageTypes.HOME && ppeUspCountdown.text && uspBarRef.current)) {
        return;
      }

      if (window.scrollY === 0 && !uspBarRef.current.classList.contains('zeroHeight')) {
        uspBarRef.current.classList.add('zeroHeight');
      } else {
        uspBarRef.current.classList.remove('zeroHeight');
        uspBarRef.current.classList.add('sticky');
      }
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [pageType, ppeUspCountdown.text]);

  useEffect(() => {
    const intervalId =
      enableUspScroll && isMobile && usps.length > 1
        ? setInterval(() => {
            setActiveIndex(prevIndex => (prevIndex === usps.length - 1 ? 0 : prevIndex + 1));
          }, fullCycleTime)
        : undefined;

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [enableUspScroll, isMobile, usps.length]);

  return (
    <>
      {(usps.length !== 0 || ppeUspCountdown.text) && (
        <S.Wrapper
          ref={uspBarRef}
          $colorScheme={colorScheme}
          $isVisible={isVisible && !fullHeightGalleryZoomIn}
          $isSticky={isSticky}
          id={isVisible && isSticky ? 'uspBar' : undefined}
        >
          <S.USPBarList data-testid="usp-list" data-cs-capture="">
            {ppeUspCountdown.countdown?.endDate && (
              <USPItemContainer suppressHydrationWarning className="ppe">
                <S.PpeUspCountdown
                  $colorScheme={colorScheme}
                  endTime={ppeUspCountdown.countdown.endDate}
                  text={ppeUspCountdown.text}
                  colorScheme={colorScheme}
                  shouldAddLeadingZero
                />
              </USPItemContainer>
            )}
            {usps.map((item, index) => {
              if ('countdown' in item) {
                return (
                  !ppeUspCountdown.text && (
                    <USPItemContainer
                      suppressHydrationWarning
                      key={index}
                      url={item.link?.url}
                      target={item.link?.target}
                      className={`${
                        enableUspScroll && isMobile && usps.length > 1 ? 'scrollable' : ''
                      } ${
                        index === activeIndex && enableUspScroll && isMobile && usps.length > 1
                          ? 'active'
                          : ''
                      }`}
                    >
                      <USPCountdown
                        endTime={convertStringToDate(
                          item.countdown?.endDate ?? '',
                          item.countdown?.timezone ?? 'UTC+2 (CEST)'
                        )}
                        text={item.text}
                        colorScheme={colorScheme}
                      />
                    </USPItemContainer>
                  )
                );
              }

              return (
                <USPItemContainer
                  suppressHydrationWarning
                  key={index}
                  url={item.link?.url}
                  target={item.link?.target}
                  className={`${
                    !ppeUspCountdown.text && enableUspScroll && isMobile && usps.length > 1
                      ? 'scrollable'
                      : ''
                  } ${
                    !ppeUspCountdown.text &&
                    index === activeIndex &&
                    enableUspScroll &&
                    isMobile &&
                    usps.length > 1
                      ? 'active'
                      : ''
                  }`}
                  colorScheme={colorScheme}
                >
                  <USPItem
                    iconName={item.icon}
                    title={item.text?.title}
                    description={item.text?.description}
                    colorScheme={colorScheme}
                  />
                </USPItemContainer>
              );
            })}
          </S.USPBarList>
        </S.Wrapper>
      )}
    </>
  );
};
